"use client";

import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-border bg-card">
        <p className="font-default text-2xl text-foreground italic mb-4">{successMessage}</p>
        <p className="text-sm text-muted-foreground mb-6 font-default">Nos pondremos en contacto contigo en un plazo de tres días hábiles.</p>
        <Button onClick={resetForm} variant="outline" className="">
          Enviar otra solicitud
        </Button>
      </div>;
  }
  return <div className="bg-card border border-border p-8 md:p-12">
      <Form onSubmit={handleSubmit} className="space-y-8">
        <FormField control={control} name="name" rules={{
        required: "Por favor, comparte tu nombre"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs tracking-[0.2em] uppercase font-default font-medium text-muted-foreground">
                Nombre
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Jane Doe" className="border-0 border-b border-border bg-transparent px-0 h-12 text-lg font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Se requiere una dirección de correo electrónico",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Por favor, introduce un correo electrónico válido"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs tracking-[0.2em] uppercase font-default font-medium text-muted-foreground">
                Correspondencia
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="jane@example.com" className="border-0 border-b border-border bg-transparent px-0 h-12 text-lg font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Un número de teléfono nos ayuda a coordinar"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs tracking-[0.2em] uppercase font-default font-medium text-muted-foreground">
                Teléfono
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="+34 600 000 000" className="border-0 border-b border-border bg-transparent px-0 h-12 text-lg font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" rules={{
        required: "Por favor, indica una fecha preferida"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs tracking-[0.2em] uppercase font-default font-medium text-muted-foreground">
                Fecha Preferida
              </FormLabel>
              <FormControl>
                <Input {...field} type="date" className="border-0 border-b border-border bg-transparent px-0 h-12 text-lg font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guests" rules={{
        required: "Por favor, indica el número de invitados",
        min: {
          value: 1,
          message: "Mínimo un invitado"
        },
        max: {
          value: 20,
          message: "Máximo veinte invitados"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs tracking-[0.2em] uppercase font-default font-medium text-muted-foreground">
                Número de Invitados
              </FormLabel>
              <FormControl>
                <Input {...field} type="number" min={1} max={20} placeholder="1" className="border-0 border-b border-border bg-transparent px-0 h-12 text-lg font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_location" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs tracking-[0.2em] uppercase font-default font-medium text-muted-foreground">
                Ubicación Preferida
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 h-12 text-lg font-default text-foreground focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 w-full transition-colors data-[size=default]:h-12">
                    <SelectValue placeholder="Selecciona una ubicación" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="valencia" className="font-default text-base">Valencia</SelectItem>
                  <SelectItem value="madrid" className="font-default text-base">Madrid</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs tracking-[0.2em] uppercase font-default font-medium text-muted-foreground">
                Una Nota para Niki
              </FormLabel>
              <FormControl>
                <Textarea {...field} rows={4} placeholder="Cuéntame sobre tu evento o sesión..." className="border-0 border-b border-border bg-transparent px-0 py-2 min-h-[7rem] text-lg font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 resize-none transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="agreed" rules={{
        required: "Por favor, confirma que has leído los términos de reserva"
      }} render={({
        field
      }) => <FormItem className="gap-2">
              <div className="flex items-start gap-3 pt-2">
                <FormControl>
                  <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-card" />
                </FormControl>
                <FormLabel className="font-default text-sm text-foreground/80 italic leading-relaxed cursor-pointer">
                  He leído y acepto los términos de reserva
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 border border-destructive/30 bg-destructive/5">
            <p className="text-destructive text-sm font-default">{submitError}</p>
          </div>}

        <div className="pt-2">
          <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-lg">
            {isSubmitting ? "Enviando tu solicitud..." : "Enviar tu solicitud"}
            <ArrowUpRight className="ml-1 size-5" />
          </Button>
          <p className="text-xs text-muted-foreground font-default mt-4 text-center italic">
            Tus datos se mantienen en estricta confidencialidad.
          </p>
        </div>
      </Form>
    </div>;
}
export default function ScheduleYourSession() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="2213" id="schedule-your-session" className="bg-background text-foreground py-24 md:py-36 px-6 md:px-10">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-2">
            <p className="text-xs tracking-[0.25em] uppercase font-default font-medium mb-8 text-primary">
              Solo Con Cita Previa
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.05] mb-8">
              Solicita una{" "}
              <span className="italic text-primary">
                sesión
              </span>
              {" "}profesional.
            </h2>
            <div className="max-w-sm">
              <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-6">Nicole Barrios proporciona servicios de cabello y maquillaje de calidad editorial para sesiones de modelos profesionales y eventos especiales. Cada sesión se adapta a tu estética única, garantizando un acabado pulido y natural.</p>
              <p className="font-default italic text-base text-primary">
                Respuestas dentro de tres días hábiles.
              </p>
            </div>

            <div className="hidden lg:block mt-16 pt-10 border-t border-border">
              <p className="text-xs tracking-[0.2em] uppercase font-default font-medium text-muted-foreground mb-3">
                Discreción
              </p>
              <p className="font-default italic text-lg text-foreground/90 leading-relaxed">
                "Un estudio de consideraciones silenciosas, donde cada rostro es un lienzo."
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <FormProvider formKey="schedule_session_booking" formId="schedule_session_booking" sectionName="schedule-your-session" defaultValues={{
            name: "",
            email: "",
            phone: "",
            preferred_date: "",
            guests: "",
            preferred_location: "",
            message: "",
            agreed: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            preferred_date: {
              required: true
            },
            guests: {
              required: true,
              min: 1,
              max: 20
            },
            message: {
              maxLength: 1000
            }
          }} submitText="Enviar tu solicitud" successMessage="Tu consulta ha sido recibida. Responderé en un plazo de tres días hábiles." errorMessage="No pudimos enviar tu solicitud. Por favor, inténtalo de nuevo en breve.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}